import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import ServiceCard from "@/components/ServiceCard";
import CTASection from "@/components/CTASection";
import QuoteCalculator from "@/components/QuoteCalculator";
import { fetchServices } from "@/lib/api";
import PageHero from "@/components/PageHero";

// Module-level constants to avoid re-creating animation config on every render.
const FADE_UP_INITIAL = { opacity: 0, y: 20 };
const FADE_UP_ANIMATE = { opacity: 1, y: 0 };
const VIEWPORT_ONCE_MARGIN = { once: true, margin: "-50px" };

export default function Services() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        document.title = "Surveys2CAD | Services";
        let cancelled = false;
        setLoading(true);
        setError(false);
        fetchServices()
            .then((s) => {
                if (cancelled) return;
                setServices(s || []);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setError(true);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, []); // fetchServices + setState setters are stable — mount-only fetch, safe to omit from deps.

    return (
        <div>
            {/* HERO — services variant (property boundary + compass) */}
            <PageHero variant="services" tint="cyan">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 md:py-24">
                    <div className="max-w-3xl">
                        <div className="overline mb-3">Our full catalog</div>
                        <h1 className="font-heading text-5xl sm:text-6xl font-bold text-slate-50 tracking-tighter leading-[1.05]">
                            Land Surveying <br />
                            <span
                                className="bg-clip-text text-transparent"
                                style={{ backgroundImage: "linear-gradient(120deg, #22d3ee 0%, #3b82f6 45%, #a855f7 100%)" }}
                            >
                                CAD Drafting Services.
                            </span>
                        </h1>
                        <p className="text-slate-400 mt-6 text-lg leading-relaxed">
                            Twelve specialized deliverables covering every stage of a survey project lifecycle — from acquisition
                            to recording. Native DWG/DXF outputs, stamped PDFs, and 3D models, produced to your office standards.
                        </p>
                    </div>
                </div>
            </PageHero>

            {/* GRID — violet tint */}
            <section className="bg-section-violet section-divider relative">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 md:py-20">
                    {loading && (
                        <div
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
                            data-testid="services-loading"
                            aria-busy="true"
                        >
                            {Array.from({ length: 9 }).map((_, i) => (
                                <div
                                    key={`service-skeleton-${i}`}
                                    className="tech-card p-6 md:p-7 animate-pulse"
                                >
                                    <div className="w-11 h-11 rounded-lg bg-slate-800/60 mb-5" />
                                    <div className="h-5 w-2/3 bg-slate-800/60 rounded mb-3" />
                                    <div className="h-3 w-full bg-slate-800/40 rounded mb-2" />
                                    <div className="h-3 w-4/5 bg-slate-800/40 rounded" />
                                </div>
                            ))}
                        </div>
                    )}

                    {!loading && error && (
                        <div
                            className="tech-card p-8 text-center"
                            data-testid="services-error"
                        >
                            <div className="text-slate-200 font-heading text-xl mb-2">
                                Couldn't load services right now
                            </div>
                            <p className="text-slate-400 text-sm mb-5">
                                Please refresh the page — or browse our most requested services below.
                            </p>
                            <button
                                onClick={() => window.location.reload()}
                                className="btn-primary"
                                data-testid="services-retry-btn"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {!loading && !error && services.length === 0 && (
                        <div className="tech-card p-8 text-center" data-testid="services-empty">
                            <p className="text-slate-300">No services available at the moment. Please check back soon.</p>
                        </div>
                    )}

                    {!loading && !error && services.length > 0 && (
                        <div
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
                            data-testid="services-grid"
                        >
                            {services.map((s, i) => (
                                <motion.div
                                    key={s.slug}
                                    initial={FADE_UP_INITIAL}
                                    whileInView={FADE_UP_ANIMATE}
                                    viewport={VIEWPORT_ONCE_MARGIN}
                                    transition={{ delay: (i % 3) * 0.08 }}
                                >
                                    <ServiceCard service={s} />
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* STANDARDS + CALCULATOR — amber tint */}
            <section className="bg-section-amber section-divider relative">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
                    <div className="lg:col-span-2">
                        <div className="overline mb-3">Delivery standards</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 tracking-tight">
                            Software, formats, and standards we deliver in.
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
                            {["AutoCAD (DWG/DXF)", "Civil 3D", "Revit", "MicroStation", "ArcGIS (SHP/GDB)", "LandXML", "Point Cloud (LAS/RCP)", "Stamped PDF", "Google Earth (KML/KMZ)"].map(
                                (item) => (
                                    <div
                                        key={item}
                                        className="tech-card p-4 text-sm text-slate-200 font-mono-alt"
                                        data-testid={`format-${item}`}
                                    >
                                        {item}
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                    <QuoteCalculator />
                </div>
            </section>

            {/* CTA — emerald */}
            <section className="bg-section-emerald section-divider relative">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 md:py-20">
                    <CTASection />
                </div>
            </section>
        </div>
    );
}
